import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { setViewMode, selectTask } from '../../store/slices/scheduledTaskSlice';
import { scheduledTaskService } from '../../services/scheduledTask';
import { i18nService } from '../../services/i18n';
import TaskList from './TaskList';
import TaskForm from './TaskForm';
import TaskDetail from './TaskDetail';
import AllRunsHistory from './AllRunsHistory';
import DeleteConfirmModal from './DeleteConfirmModal';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import SidebarToggleIcon from '../icons/SidebarToggleIcon';
import ComposeIcon from '../icons/ComposeIcon';
import WindowTitleBar from '../window/WindowTitleBar';

interface ScheduledTasksViewProps {
  isSidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  onNewChat?: () => void;
}

type TabType = 'tasks' | 'history';

const ScheduledTasksView: React.FC<ScheduledTasksViewProps> = ({
  isSidebarCollapsed,
  onToggleSidebar,
  onNewChat,
}) => {
  const dispatch = useDispatch();
  const isMac = window.electron.platform === 'darwin';
  const viewMode = useSelector((state: RootState) => state.scheduledTask.viewMode);
  const selectedTaskId = useSelector((state: RootState) => state.scheduledTask.selectedTaskId);
  const tasks = useSelector((state: RootState) => state.scheduledTask.tasks);
  const allRuns = useSelector((state: RootState) => state.scheduledTask.allRuns);
  const selectedTask = selectedTaskId ? tasks.find((t) => t.id === selectedTaskId) ?? null : null;
  const [activeTab, setActiveTab] = useState<TabType>('tasks');
  const [deleteTaskInfo, setDeleteTaskInfo] = useState<{ id: string; name: string } | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearingHistory, setIsClearingHistory] = useState(false);
  const [clearHistoryError, setClearHistoryError] = useState('');
  const clearableCount = allRuns.filter((run) => run.status === 'success' || run.status === 'error').length;

  // 定时任务跟着侧边栏选的执行环境走：选了桌面设备就列那台机器上的任务，
  // 隔着 relay 读它本机的调度器，而不是拿云端的列表冒充。
  const selectedDesktopClientId = useSelector((state: RootState) => state.cowork.selectedDesktopClientId);
  const selectedDesktopClientName = useSelector((state: RootState) => state.cowork.selectedDesktopClientName);
  const loadError = useSelector((state: RootState) => state.scheduledTask.error);
  const isDesktopEnv = !!selectedDesktopClientId;
  // 桌面端版本太旧、没有定时任务通道时，relay 回 UNKNOWN_ACTION。这跟「一条任务
  // 都没有」是两回事，要分开说，否则用户会以为任务丢了。
  const desktopUnsupported = isDesktopEnv && !!loadError && loadError.includes('UNKNOWN_ACTION');

  // 环境切换即换数据源。任务 id 在两个环境里各自独立，残留一条上个环境的任务，
  // 点开就会对着另一台机器发请求，所以先退回列表再换源。
  useEffect(() => {
    dispatch(selectTask(null));
    dispatch(setViewMode('list'));
    scheduledTaskService.setTargetClient(selectedDesktopClientId);
  }, [selectedDesktopClientId, dispatch]);

  const handleRequestDelete = useCallback((taskId: string, taskName: string) => {
    setDeleteTaskInfo({ id: taskId, name: taskName });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTaskInfo) return;
    const taskId = deleteTaskInfo.id;
    setDeleteTaskInfo(null);
    await scheduledTaskService.deleteTask(taskId);
    // If we were viewing this task's detail, go back to list
    if (selectedTaskId === taskId) {
      dispatch(selectTask(null));
      dispatch(setViewMode('list'));
    }
  }, [deleteTaskInfo, selectedTaskId, dispatch]);

  const handleCancelDelete = useCallback(() => {
    setDeleteTaskInfo(null);
  }, []);

  const handleOpenClearConfirm = useCallback(() => {
    setClearHistoryError('');
    setShowClearConfirm(true);
  }, []);

  const handleCloseClearConfirm = useCallback(() => {
    if (isClearingHistory) return;
    setShowClearConfirm(false);
    setClearHistoryError('');
  }, [isClearingHistory]);

  const handleClearHistory = useCallback(async () => {
    if (isClearingHistory) return;
    setIsClearingHistory(true);
    setClearHistoryError('');
    try {
      await scheduledTaskService.clearRunHistory();
      await scheduledTaskService.loadAllRuns(50);
      setShowClearConfirm(false);
    } catch {
      setClearHistoryError(i18nService.t('scheduledTasksClearHistoryFailed'));
    } finally {
      setIsClearingHistory(false);
    }
  }, [isClearingHistory]);

  useEffect(() => {
    scheduledTaskService.loadTasks();
  }, []);

  const handleBackToList = () => {
    dispatch(selectTask(null));
    dispatch(setViewMode('list'));
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === 'tasks') {
      dispatch(selectTask(null));
      dispatch(setViewMode('list'));
    }
  };

  // Show tabs only in list view (not in create/edit/detail sub-views)
  const showTabs = viewMode === 'list' && !selectedTaskId;

  const deviceLabel = selectedDesktopClientName || i18nService.t('envSelectorDesktopClient');

  const desktopUnsupportedNotice = (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-8 py-16 text-center">
      <p className="text-sm font-medium dark:text-claude-darkText text-claude-text">
        {i18nService.t('scheduledTasksDesktopEnvTitle')}
      </p>
      <p className="max-w-md text-xs leading-5 dark:text-claude-darkTextSecondary text-claude-textSecondary">
        {i18nService.t('scheduledTasksDesktopEnvHint').replace('{device}', deviceLabel)}
      </p>
    </div>
  );

  // 能读能控，但不能在网页端新建/编辑：桌面端任务要填工作目录、执行模式这些本机
  // 概念，这张表单没有这些字段。说清楚比给一个会失败的按钮好。
  const desktopEnvBanner = (
    <div className="mx-4 mt-3 rounded-lg border border-claude-border/70 bg-claude-surfaceHover/40 px-3 py-2 text-xs leading-5 text-claude-textSecondary dark:border-claude-darkBorder/70 dark:bg-claude-darkSurfaceHover/40 dark:text-claude-darkTextSecondary">
      {i18nService.t('scheduledTasksDesktopEnvBanner').replace('{device}', deviceLabel)}
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="draggable flex h-12 shrink-0 items-center justify-between px-4">
        <div className="flex items-center space-x-3 h-8">
          {isSidebarCollapsed && (
            <div className={`non-draggable flex items-center gap-1 ${isMac ? 'pl-[68px]' : ''}`}>
              <button
                type="button"
                onClick={onToggleSidebar}
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
              >
                <SidebarToggleIcon className="h-4 w-4" isCollapsed={true} />
              </button>
              <button
                type="button"
                onClick={onNewChat}
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
              >
                <ComposeIcon className="h-4 w-4" />
              </button>
            </div>
          )}
          {viewMode !== 'list' && (
            <button
              onClick={handleBackToList}
              className="non-draggable p-2 rounded-lg dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary text-claude-textSecondary transition-colors"
              aria-label={i18nService.t('back')}
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
          )}
          <h1 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
            {i18nService.t('scheduledTasksTitle')}
          </h1>
        </div>
        <WindowTitleBar inline />
      </div>

      {/* Tabs + New Task button */}
      {showTabs && (
        <div className="flex shrink-0 items-center justify-between px-4">
          <div className="flex">
            <button
              type="button"
              onClick={() => handleTabChange('tasks')}
              className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                activeTab === 'tasks'
                  ? 'dark:text-claude-darkText text-claude-text'
                  : 'dark:text-claude-darkTextSecondary text-claude-textSecondary hover:dark:text-claude-darkText hover:text-claude-text'
              }`}
            >
              {i18nService.t('scheduledTasksTabTasks')}
              {activeTab === 'tasks' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-claude-accent rounded-t" />
              )}
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('history')}
              className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                activeTab === 'history'
                  ? 'dark:text-claude-darkText text-claude-text'
                  : 'dark:text-claude-darkTextSecondary text-claude-textSecondary hover:dark:text-claude-darkText hover:text-claude-text'
              }`}
            >
              {i18nService.t('scheduledTasksTabHistory')}
              {activeTab === 'history' && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-claude-accent rounded-t" />
              )}
            </button>
          </div>
          {activeTab === 'tasks' && !isDesktopEnv && (
            <button
              type="button"
              onClick={() => dispatch(setViewMode('create'))}
              className="px-3 py-1 text-sm font-medium bg-claude-accent text-white rounded-lg hover:bg-claude-accentHover transition-colors"
            >
              {i18nService.t('scheduledTasksNewTask')}
            </button>
          )}
          {activeTab === 'history' && !isDesktopEnv && clearableCount > 0 && (
            <button
              type="button"
              onClick={handleOpenClearConfirm}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-claude-textSecondary transition-colors hover:bg-red-50 hover:text-red-600 dark:text-claude-darkTextSecondary dark:hover:bg-red-500/10 dark:hover:text-red-400"
            >
              <TrashIcon className="h-3.5 w-3.5" />
              {i18nService.t('scheduledTasksClearHistory')}
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {desktopUnsupported ? (
          desktopUnsupportedNotice
        ) : showTabs && activeTab === 'history' ? (
          <AllRunsHistory />
        ) : (
          <>
            {viewMode === 'list' && isDesktopEnv && desktopEnvBanner}
            {viewMode === 'list' && <TaskList onRequestDelete={handleRequestDelete} />}
            {viewMode === 'create' && (
              <TaskForm
                mode="create"
                onCancel={handleBackToList}
                onSaved={handleBackToList}
              />
            )}
            {viewMode === 'edit' && selectedTask && (
              <TaskForm
                mode="edit"
                task={selectedTask}
                onCancel={() => dispatch(setViewMode('detail'))}
                onSaved={() => dispatch(setViewMode('detail'))}
              />
            )}
            {viewMode === 'detail' && selectedTask && (
              <TaskDetail task={selectedTask} onRequestDelete={handleRequestDelete} />
            )}
          </>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteTaskInfo && (
        <DeleteConfirmModal
          taskName={deleteTaskInfo.name}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}

      {showClearConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4 backdrop-blur-[1px]"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) handleCloseClearConfirm();
          }}
        >
          <div className="w-full max-w-sm rounded-2xl border border-claude-border bg-white p-5 shadow-xl dark:border-claude-darkBorder dark:bg-claude-darkSurface">
            <h3 className="text-base font-semibold text-claude-text dark:text-claude-darkText">
              {i18nService.t('scheduledTasksClearHistoryConfirmTitle')}
            </h3>
            <p className="mt-2 text-sm leading-6 text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {i18nService.t('scheduledTasksClearHistoryConfirmMessage')}
            </p>
            {clearHistoryError && (
              <p className="mt-3 text-xs text-red-500 dark:text-red-400">{clearHistoryError}</p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={isClearingHistory}
                onClick={handleCloseClearConfirm}
                className="h-9 rounded-lg px-3 text-sm text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover disabled:cursor-not-allowed disabled:opacity-50 dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover"
              >
                {i18nService.t('cancel')}
              </button>
              <button
                type="button"
                disabled={isClearingHistory}
                onClick={() => void handleClearHistory()}
                className="h-9 rounded-lg bg-red-500 px-3 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isClearingHistory
                  ? i18nService.t('scheduledTasksClearingHistory')
                  : i18nService.t('scheduledTasksClearHistory')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledTasksView;
